import { ChangeEvent, MouseEvent } from 'react';
import { observable, action, runInAction, toJS } from 'mobx';
import URI from 'urijs';
import CryptoJS from 'crypto-js';
// @ts-ignore
import bytes from 'utf8-bytes';
import { toast } from 'react-toastify';
import { isEmpty, trim, debounce } from 'lodash';
import posthog from 'posthog-js';

import Service from '@service/login';
import { testEmail } from '@tool/index';
import moLogin from './login';
import { toggleValue } from './index';
import Cookies from 'js-cookie';
import { buildRegisterParams } from './register-params';
import { getRegistrationAttribution } from '@lib/registration-attribution';

const moRegister = observable({
  company: '', // 所在公司
  email: '', // 注册邮箱
  phone: '', // 手机号码
  captcha: '', // 短信验证码
  password: '', // 密码
  companyList: <any[]>[],
  industry: '', // 行业
  city: '', // 城市
  nature: '', // 企业性质
  scale: '', // 企业规模
  // 非必填项
  nickname: '', // 昵称
  inviteCode: '', // 邀请码
  qrText: '获取验证码',
  qrClass: '',
  companyEmpty: false,
  emailEmpty: false,
  phoneEmpty: false,
  captchaEmpty: false,
  passwordEmpty: false,
  phoneErrorText: '手机号码不能为空',
  emailErrorText: '邮箱不能为空',
  isPop: false,
});

const reset = action(() => {
  moRegister.company = '';
  moRegister.email = '';
  moRegister.phone = '';
  moRegister.captcha = '';
  moRegister.password = '';
  moRegister.nickname = '';
  moRegister.inviteCode = '';
  moRegister.companyEmpty = false;
  moRegister.emailEmpty = false;
  moRegister.captchaEmpty = false;
  moRegister.passwordEmpty = false;
});

// 输入所在公司
const changeCompany = action(async (value: string) => {
  // console.log('所在公司 -- ', value);
  moRegister.companyEmpty = false;
  moRegister.company = value;

  if (!trim(value)) return;
  const arr: any[] = await Service.searchCompany(value);
  // console.log('公司列表 -- ', arr);
  runInAction(() => { moRegister.companyList = arr; });
});

// 选择公司
const chooseCompany = action((item: any) => {
  moRegister.industry = '';
  moRegister.city = '';
  moRegister.nature = '';
  moRegister.scale = '';
  moRegister.companyEmpty = false;
  moRegister.company = item.company || '';
});

// 输入注册邮箱
const changeEmail = action((ev: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
  // console.log('邮箱 -- ', ev.target.value);
  ev.stopPropagation();
  ev.preventDefault();
  moRegister.emailEmpty = false;
  moRegister.email = ev.target.value;
});

// 输入手机号码
const changePhone = action((ev: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
  // console.log('手机号码 -- ', ev.target.value);
  if (ev.target.value.length > 11) return;
  moRegister.phoneEmpty = false;
  moRegister.phone = ev.target.value;
});

// 输入短信验证码
const changeCaptcha = action((ev: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
  // console.log('验证码 -- ', ev.target.value);
  if (ev.target.value.length > 4) return;
  moRegister.captchaEmpty = false;
  moRegister.captcha = ev.target.value;
});

// 输入密码
const changePassword = action((ev: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
  // console.log('密码 -- ', ev.target.value);
  moRegister.passwordEmpty = false;
  moRegister.password = ev.target.value;
});

// 输入昵称
const changeNickname = action((ev: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
  // console.log('昵称 -- ', ev.target.value);
  moRegister.nickname = ev.target.value;
});

// 输入邀请码
const changeInviteCode = action((ev: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
  // console.log('邀请码 -- ', ev.target.value);
  moRegister.inviteCode = ev.target.value;
});

// 倒计时
const countDown = action(async (num: number) => {
  let newNumber: number = num;
  runInAction(() => {
    moRegister.qrText = newNumber.toString();
    moRegister.qrClass = 'disable-text60';

    const timer = setInterval(() => {
      newNumber -= 1;
      runInAction(() => {
        moRegister.qrText = newNumber.toString();
        if (newNumber <= 0) {
          clearInterval(timer);
          moRegister.qrClass = '';
          moRegister.qrText = '获取验证码';
        }
      });
    }, 1000);
  });
});

// 获取验证码
const getCaptcha = action(async (ev: MouseEvent) => {
  ev.stopPropagation();
  if (!trim(moRegister.phone)) {
    moRegister.phoneErrorText = '手机号码不能为空';
    moRegister.phoneEmpty = true;
    return;
  }
  if (trim(moRegister.phone).length !== 11) {
    moRegister.phoneErrorText = '请输入有效的手机号码';
    moRegister.phoneEmpty = true;
    return;
  }
  if (moRegister.qrText !== '获取验证码') return;
  const params = { phone: moRegister.phone };
  const res: any = await Service.msgCaptcha(params);
  if (isEmpty(res)) return;
  if (res.code === 0) {
    toast.success('验证码发送成功', {
      theme: 'dark',
      position: toast.POSITION.TOP_CENTER,
      autoClose: 2000,
      draggable: false,
      pauseOnFocusLoss: false,
    });
    countDown(60);
  }
});

// 去注册
const toRegister = action(async (callback: () => any) => {
  const {
    company, email, phone, captcha, password,
    nickname, inviteCode,
  } = moRegister;
  toggleValue('isLoading', true);

  const newURL = new URI(window.location.href);
  const attribution = getRegistrationAttribution(
    window.location.search,
    sessionStorage.getItem('registerQuery'),
    document.referrer,
  );
  const activityUuid = newURL.query(true).activity_uuid || '';

  // ------ 密码加密 ------
  let plaintext = password;
  const length = (16 - bytes(plaintext).length % 16) % 16;
  for (let i = 0; i < length; i += 1) plaintext += ' ';
  const pwdString = CryptoJS.enc.Utf8.parse(plaintext);
  const newPwd = CryptoJS.AES.encrypt(pwdString, CryptoJS.enc.Utf8.parse('/H0w<c|:dYV(.q)b'), {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.ZeroPadding,
    iv: CryptoJS.enc.Utf8.parse('/H0w<c|:dYV(.q)b'),
  }).toString();
  // const params = { user, password: newPwd, from };
  // console.log('new password -- ', newPwd);
  // ------ 密码加密 ------

  localStorage.setItem('account', email);
  localStorage.setItem('phone', phone);
  localStorage.setItem('password', password);

  const params = buildRegisterParams(
    { company, email, phone, captcha, inviteCode },
    {
      captchaOverride: 'xxxx',
      activityUuid,
      from: attribution.from,
      utmParams: attribution.utm_params,
    },
  );
  const res: any = await Service.register(params);
  toggleValue('isLoading', false);
  if (res.code !== 0) {
    toast.error(res.msg, {
      theme: 'dark',
      position: toast.POSITION.TOP_CENTER,
      autoClose: 2000,
      draggable: false,
      pauseOnFocusLoss: false,
    });
    return;
  }
  Cookies.set('access_token', res.result?.access_token || '');
  sessionStorage.removeItem('registerQuery');
  posthog.capture('注册成功人数', { email: email });
  localStorage.setItem('regIndustry', moRegister.industry || '');
  localStorage.setItem('regCity', moRegister.city);
  localStorage.setItem('regNature', moRegister.nature);
  localStorage.setItem('regScale', moRegister.scale);

  toast.success('注册成功', {
    theme: 'dark',
    position: toast.POSITION.TOP_CENTER,
    autoClose: 1000,
    draggable: false,
    pauseOnFocusLoss: false,
    onClose: callback(),
  });
});

// 注册
const onRegister = action((callback: () => any) => {
  const {
    company, email, phone, captcha, password,
    nickname, inviteCode,
  } = moRegister;
  if (!trim(company)) {
    moRegister.companyEmpty = true;
    return;
  }
  if (!trim(email)) {
    moRegister.emailErrorText = '邮箱不能为空';
    moRegister.emailEmpty = true;
    return;
  }
  // if (!testEmail(trim(moRegister.email))) {
  //   moRegister.emailErrorText = '邮箱格式不正确';
  //   moRegister.emailEmpty = true;
  //   return;
  // }
  if (!trim(phone)) {
    moRegister.phoneErrorText = '手机号码不能为空';
    moRegister.phoneEmpty = true;
    return;
  }
  if (trim(moRegister.phone).length !== 11) {
    moRegister.phoneErrorText = '请输入有效的手机号码';
    moRegister.phoneEmpty = true;
    return;
  }
  // if (!trim(captcha)) {
  //   moRegister.captchaEmpty = true;
  //   return;
  // }
  // if (!trim(password)) {
  //   moRegister.passwordEmpty = true;
  //   return;
  // }
  toRegister(callback);
});

// 清空公司列表
const resetCompanyList = action(() => {
  moRegister.companyList = [];
});

const togglePop = action((value: boolean) => {
  moRegister.isPop = value;
});

export default moRegister;
export {
  changeCompany, changeEmail, changePhone, changeCaptcha,
  changePassword, changeNickname, changeInviteCode, chooseCompany,
  getCaptcha, onRegister, resetCompanyList, togglePop, reset,
};
